import { Fragment, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Transition } from "@headlessui/react";
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { useStateContext } from "../../contexts/ContextProvider";

const KIND_ICONS = {
    success: <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />,
    error: <XMarkIcon className="h-6 w-6 text-red-400" aria-hidden="true" />,
    warning: <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />,
    info: <InformationCircleIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />,
} as const;

/**
 * Toast unique de l'application, alimenté par `setNotifications()`.
 *
 * Monté une seule fois à la racine (main.tsx) et porté dans <body> au-dessus de
 * toute l'échelle des overlays : la Modal partagée monte à 1040 (`.modal-overlay`)
 * et certaines modales à 10000 — un toast à z-50 dans le layout restait caché
 * derrière dès qu'une fenêtre était ouverte.
 */
type ToastKind = keyof typeof KIND_ICONS;
type ToastNotification = Partial<Record<ToastKind, string>>;

const kindOf = (notification: ToastNotification | null | undefined): ToastKind | undefined =>
    (Object.keys(KIND_ICONS) as ToastKind[]).find((k) => notification?.[k]);

/**
 * Dernière notification affichable. À la fermeture (`null`), la transition de
 * sortie garde le cadre visible ~100 ms : sans ce dernier message, il s'estompait
 * VIDE (cadre blanc sans icône ni texte).
 */
export function useDisplayedNotification(notifications: ToastNotification | null | undefined) {
    const lastRef = useRef<ToastNotification | null>(null);
    const kind = kindOf(notifications);
    if (kind) {
        lastRef.current = notifications ?? null;
    }

    return { show: Boolean(kind), displayed: lastRef.current, displayedKind: kindOf(lastRef.current) };
}

export default function GlobalToast() {
    const { t } = useTranslation("common");
    const { notifications, setNotifications } = useStateContext();
    const { show, displayed, displayedKind: kind } = useDisplayedNotification(notifications);

    return createPortal(
        <div
            aria-live="assertive"
            className="pointer-events-none fixed inset-x-0 top-0 z-toast flex items-start justify-center px-4 py-4"
        >
            <div className="flex w-full flex-col items-center space-y-4">
                <Transition
                    show={show}
                    as={Fragment}
                    enter="transform ease-out duration-300 transition"
                    enterFrom="translate-y-2 opacity-0"
                    enterTo="translate-y-0 opacity-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="p-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">{kind && KIND_ICONS[kind]}</div>
                                <div className="ml-3 w-0 flex-1 pt-0.5">
                                    <p className="text-sm text-gray-500">{kind && displayed?.[kind]}</p>
                                </div>
                                <div className="ml-4 flex flex-shrink-0">
                                    <button
                                        type="button"
                                        className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2"
                                        onClick={() => setNotifications(null)}
                                    >
                                        <span className="sr-only">{t("actions.close")}</span>
                                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>,
        document.body
    );
}
